import DOMPurify from "dompurify";
import {
  SIGNATURE_VARIABLE_KEY,
  SIGNATURE_DATE_VARIABLE_KEY,
} from "app/containers/ContractManagement/variables";
import "./contractView.css";

// Read-only contract rendering: fills the same single-brace tokens the backend
// fills when it renders the PDF.

/**
 * Placeholder values for previews that have no job. NONE of this is ever
 * sent — a real contract is filled with the job's own data by the backend.
 *
 * Mirrors ContractAPI `constants.SAMPLE_PREVIEW_DATA`, the fixture the backend
 * previews with, so this preview and the PDF preview agree.
 */
export const SAMPLE_CONTRACT_DATA = {
  Client_Name: "John Doe",
  Client_Email: "john.doe@example.com",
  Property_Address: "123 Main St, San Francisco, CA 94102",
  // Old alias of Property_Address. Templates not yet re-saved still carry
  // this token and need a value here.
  Job_Address: "123 Main St, San Francisco, CA 94102",
  Total_Fee: "$550.00",
  Inspection_Date: "January 31, 2026",
  Service_Type: "Home Inspection",
  Agent_Name: "Jane Smith",
};

const PLACEHOLDER_REGEX = /\{([A-Za-z0-9_]+)\}/g;

// Values are NOT bolded: the PDF renders filled-in data as normal-weight text,
// so any bold comes only from `<strong>` already in the source content.
const tokenNode = (key, data) => {
  if (key === SIGNATURE_VARIABLE_KEY) {
    const sig = document.createElement("div");
    sig.className = "contract-view-signature-line";
    sig.textContent = "Client signature — signed at send time";
    return sig;
  }
  if (key === SIGNATURE_DATE_VARIABLE_KEY) {
    const span = document.createElement("span");
    span.className = "contract-view-sign-date";
    span.textContent = "date signed";
    return span;
  }
  if (data[key] != null && data[key] !== "") {
    return document.createTextNode(data[key]);
  }
  // Unknown or empty variable — surface the raw placeholder so it is obvious
  // something is missing.
  return document.createTextNode(`[${key}]`);
};

const substituteInDom = (root, data) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  for (const textNode of textNodes) {
    const value = textNode.nodeValue || "";
    PLACEHOLDER_REGEX.lastIndex = 0;
    if (!PLACEHOLDER_REGEX.test(value)) continue;
    PLACEHOLDER_REGEX.lastIndex = 0;

    const parent = textNode.parentNode;
    if (!parent) continue;
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = PLACEHOLDER_REGEX.exec(value)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(
          document.createTextNode(value.slice(lastIndex, match.index))
        );
      }
      frag.appendChild(tokenNode(match[1], data));
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < value.length) {
      frag.appendChild(document.createTextNode(value.slice(lastIndex)));
    }
    parent.replaceChild(frag, textNode);
  }
};

// Schemes allowed for rendered links. Lexical already sanitizes author URLs
// to `about:blank` on save, but the output feeds a `dangerouslySetInnerHTML`
// sink, so they are re-checked here as defense in depth.
const SAFE_LINK_SCHEME = /^(https?:|mailto:|tel:|sms:|#|\/|\.)/i;

// Force every link in the read-only contract to open in a new tab, with
// `rel="noopener noreferrer"` so the opened page can't reach back into this
// window. Unsafe schemes (e.g. `javascript:`) have their href stripped.
const openLinksInNewTab = (root) => {
  const anchors = root.querySelectorAll("a[href]");
  for (const anchor of anchors) {
    if (!SAFE_LINK_SCHEME.test(anchor.getAttribute("href") || "")) {
      anchor.removeAttribute("href");
    }
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  }
};

/**
 * Stored template HTML → sanitized read-only HTML with its tokens filled.
 * @param {string} sourceHtml - Template content (single-brace tokens)
 * @param {Object<string, string>} [data] - Token values; defaults to sample data
 * @returns {string} Sanitized HTML for a dangerouslySetInnerHTML sink
 */
export const buildContractViewHtml = (
  sourceHtml,
  data = SAMPLE_CONTRACT_DATA
) => {
  // Sanitize first, into DOMPurify's inert document: assigning the raw source
  // to a node of this document would fire an <img onerror> even while it is
  // detached. The source can include backend-injected boilerplate, not just
  // editor output. Filling in values afterwards is safe, as tokenNode only
  // creates text and elements of its own.
  const root = DOMPurify.sanitize(sourceHtml || "", {
    ADD_ATTR: ["target"],
    RETURN_DOM: true,
  });
  substituteInDom(root, data || {});
  openLinksInNewTab(root);
  return root.innerHTML;
};
